import { useEffect, type FC } from 'react';
import toast, { Toaster, resolveValue, useToasterStore, type Toast } from 'react-hot-toast';
import { useCelular } from '../hooks/useCelular';
import './SystemToast.css';

/**
 * O toast do app.
 *
 * Proposta de 2026-09-12 (docs/designsystem/propostas/mensagens-do-sistema.html).
 *
 * Por que isto existe em vez de `toastOptions.style`: o estilo antigo era
 * `background: '#363636'` escrito no `App.tsx` — a MESMA cor nos dois temas,
 * porque era constante e não token. Aqui o markup é nosso, então o tema volta
 * a valer.
 *
 * **Nenhuma das 528 chamadas `toast.*` precisou mudar**: o `<Toaster>` aceita
 * uma função como filho e passa cada toast por ela. `toast.success` e
 * `toast.error` continuam funcionando e ganham a cara nova de graça.
 *
 * Sem ação dentro do toast (Desfazer / Tentar de novo) — decisão dele em
 * 12/09: "sem ação no momento". O toast informa; quem pergunta é o diálogo ou
 * a confirmação na linha.
 */

/** Os quatro níveis. `atencao` e `info` não existem no react-hot-toast. */
export type NivelAviso = 'ok' | 'erro' | 'atencao' | 'info';

const CLASSE_NIVEL: Record<string, NivelAviso> = {
    'ds-nivel-atencao': 'atencao',
    'ds-nivel-info': 'info',
};

const nivelDe = (t: Toast): NivelAviso => {
    /* `className` é como os níveis novos viajam: o react-hot-toast só conhece
       success / error / loading / blank. */
    const marcado = (t.className || '').split(/\s+/).find(c => CLASSE_NIVEL[c]);
    if (marcado) return CLASSE_NIVEL[marcado];
    if (t.type === 'success') return 'ok';
    if (t.type === 'error') return 'erro';
    return 'info';
};

const DsToast: FC<{ t: Toast }> = ({ t }) => {
    const nivel = nivelDe(t);
    const carregando = t.type === 'loading';

    return (
        <div
            className={`ds-toast is-${nivel} ${t.visible ? 'is-entrando' : 'is-saindo'}`}
            role={nivel === 'erro' ? 'alert' : 'status'}
            /* Clicar dispensa. Sem "X": o alvo é o toast inteiro, o que dá uma
               área de clique maior do que um ícone de 13px e tira um elemento
               de dentro de uma peça que precisa ser lida em dois segundos. */
            onClick={() => toast.dismiss(t.id)}
            title="Clique para dispensar"
        >
            {carregando
                ? <span className="ds-toast-girando" aria-hidden="true" />
                : <span className="ds-toast-ponto" aria-hidden="true" />}
            <span className="ds-toast-txt">{resolveValue(t.message, t)}</span>
        </div>
    );
};

/**
 * Fica no `App.tsx`, uma vez.
 *
 * `top-center` desde 11/10, a pedido dele: no canto direito o aviso nascia
 * em cima da coluna de ações e do painel do Brik AI, que é justamente onde o
 * olho não está depois de salvar um formulário. No centro ele cai no eixo da
 * leitura, sem tapar nada que fique fixo na direita.
 */
/** No celular, quantos avisos ficam na tela ao mesmo tempo (os mais novos). */
const LIMITE_CELULAR = 2;

/* No celular o aviso sobe do pé da tela: no topo ele tampava o cabeçalho
   (menu, lupa, sino) e o título e o X do painel ou da modal, que no celular
   ocupam a tela inteira. Fica acima da faixa de botões (Cancelar / Salvar)
   que os painéis fixam no rodapé, e no alcance do polegar para dispensar. */
const CONTAINER_CELULAR = {
    left: 12,
    right: 12,
    bottom: 'calc(84px + env(safe-area-inset-bottom))',
};

export const SystemToaster: FC = () => {
    const celular = useCelular();
    const { toasts } = useToasterStore();

    /* Quatro avisos empilhados cobriam meia tela do telefone. A loja do
       react-hot-toast guarda o mais novo primeiro; os de trás saem. */
    useEffect(() => {
        if (!celular) return;
        toasts.filter(t => t.visible).slice(LIMITE_CELULAR).forEach(t => toast.dismiss(t.id));
    }, [celular, toasts]);

    return (
        <Toaster
            position={celular ? 'bottom-center' : 'top-center'}
            gutter={celular ? 8 : 10}
            containerStyle={celular ? CONTAINER_CELULAR : undefined}
            toastOptions={{ duration: 4000 }}
        >
            {t => <DsToast t={t} />}
        </Toaster>
    );
};

export default SystemToaster;
